import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import ReactGA from 'react-ga4';
import { env } from '@config/env';
import { useCurrentUser } from '@hooks/auth/useCurrentUser';
import { useIsSuperAdmin } from '@hooks/auth/useIsSuperAdmin';
import { useIsVendor } from '@hooks/auth/useIsVendor';
import { useIsMerchant } from '@hooks/auth/useIsMerchant';

/**
 * Resolve the GA4 measurement id for the current environment. Staging and
 * production only — local/dev returns null so GA never initializes (mirrors
 * legacy App.js, which only set an id on the two ordermesh.io hosts).
 */
function resolveTrackingId(): string | null {
  if (env.isProduction) return env.VITE_GA_TRACKING_ID_PROD ?? null;
  if (env.isStaging) return env.VITE_GA_TRACKING_ID_STG ?? null;
  return null;
}

const TRACKING_ID = resolveTrackingId();

/**
 * Google Analytics (GA4) integration — port of legacy `App.js:43-101`.
 * Initializes once when a tracking id is present, sets the signed-in user's
 * dimensions (user_id / user_type / user_name) when they resolve, and sends a
 * pageview on every route change. Renders nothing. Must be mounted inside the
 * Router (for `useLocation`) and the Redux provider (for the auth selectors).
 */
export function GoogleAnalytics(): null {
  const location = useLocation();
  const user = useCurrentUser();
  const isSuperAdmin = useIsSuperAdmin();
  const isVendor = useIsVendor();
  const isMerchant = useIsMerchant();
  const initialized = useRef(false);

  // Initialize the tracker exactly once.
  useEffect(() => {
    if (TRACKING_ID && !initialized.current) {
      ReactGA.initialize(TRACKING_ID);
      initialized.current = true;
    }
  }, []);

  // Set user-specific dimensions once the signed-in user resolves. Matches the
  // legacy mapping (super admin → 'super-admin', else vendor/merchant/'').
  useEffect(() => {
    if (!TRACKING_ID || !user?.id) return;
    const userType = isSuperAdmin
      ? 'super-admin'
      : isVendor
        ? 'vendor'
        : isMerchant
          ? 'merchant'
          : '';
    ReactGA.set({
      user_id: user.id,
      user_type: userType,
      user_name: [user.firstName, user.lastName].filter(Boolean).join(' '),
    });
  }, [user?.id, user?.firstName, user?.lastName, isSuperAdmin, isVendor, isMerchant]);

  // Send a pageview on every route change.
  useEffect(() => {
    if (!TRACKING_ID) return;
    ReactGA.send({ hitType: 'pageview', page: location.pathname + location.search });
  }, [location.pathname, location.search]);

  return null;
}
